import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import NavBar from './NavBar';
import LanguageIcon from '@mui/icons-material/Language';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { CoffeeMakerOutlined, FastfoodOutlined, SupportAgentOutlined } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API } from '../utils/Api';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../src/store/Login/loginSlice';
import { useSocket } from '../context/SocketContext';
import {
  setHasNewOrder,
  fetchOrders,
  addSocketOrder,
  updateSocketOrder,
} from '../store/Orders/ordersSlice';

const drawerWidth = 130;

export default function SideBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const socket = useSocket();
  const dispatch = useDispatch();

  const outletId = localStorage.getItem('outletId');
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('role');

  const [selectedRoute, setSelectedRoute] = React.useState(location.pathname);

  const hasNewOrder = useSelector((state) => state.orders.hasNewOrder);
  const userData = useSelector((state) => state.login.user);

  const storedOutletData = localStorage.getItem('outletData');
  const outletData = storedOutletData
    ? JSON.parse(storedOutletData)
    : null;

  const userName = userData?.user?.name || '';

  const menuItems = React.useMemo(
    () => [
      {
        text: 'Orders',
        icon: <LanguageIcon />,
        route: '/onlineOrders',
      },
      {
        text: 'Dashboard',
        icon: <DashboardIcon />,
        route:
          userRole === 'CASHIER'
            ? '/dashboard'
            : '/managerDashboard',
      },
      {
        text: 'QuickServe',
        icon: <FastfoodOutlined />,
        route: '/quick-serve',
      },
      ...(!outletData?.isEventOutlet
        ? [
            {
              text: 'Products',
              icon: <CategoryOutlinedIcon />,
              route: '/products',
            },
          ]
        : []),
      // {
      //   text: 'Tutorials',
      //   icon: <SupportAgentOutlined />,
      //   route: '/helpSupport',
      // },
    ],
    [userRole, outletData]
  );

  const handleItemClick = (route) => {
    navigate(route);
    setSelectedRoute(route);

    if (route === '/onlineOrders') {
      dispatch(setHasNewOrder(false));
    }
  };

  const handleButtonClick = async () => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}outlet/user/signout`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === true) {
        toast.success('Logout successful!', {
          autoClose: 3000,
        });

        localStorage.clear();
        localStorage.setItem('isLoggedIn', 'false');

        dispatch(logout());

        navigate('/', {
          state: { message: true },
        });
      }
    } catch (error) {
      console.error('Logout Error:', error);
      toast.error('Logout failed');
    }
  };

  React.useEffect(() => {
    setSelectedRoute(location.pathname);
  }, [location.pathname]);

  React.useEffect(() => {
    dispatch(
      fetchOrders({
        allOrdersSort: 'true',
        ordersType: 'ALL',
      })
    );
  }, [dispatch]);

  React.useEffect(() => {
    if (!socket || !outletId) return;

    socket.emit('joinOutletRoom', { outletId });

    const refreshOrders = () => {
      setTimeout(() => {
        dispatch(
          fetchOrders({
            allOrdersSort: 'true',
            ordersType: 'ALL',
          })
        );
      }, 1000);
    };

    const handleOrderCreated = (newOrder) => {
      dispatch(addSocketOrder(newOrder));
      dispatch(setHasNewOrder(true));
      refreshOrders();
    };

    const handleOrderCancelled = (updatedOrder) => {
      dispatch(updateSocketOrder(updatedOrder));
      refreshOrders();
    };

    const handleOrderCompleted = (updatedOrder) => {
      dispatch(updateSocketOrder(updatedOrder));
      refreshOrders();
    };

    socket.on('orderCreated', handleOrderCreated);
    socket.on('orderCancelled', handleOrderCancelled);
    socket.on('orderCompleted', handleOrderCompleted);

    return () => {
      socket.off('orderCreated', handleOrderCreated);
      socket.off('orderCancelled', handleOrderCancelled);
      socket.off('orderCompleted', handleOrderCompleted);
    };
  }, [socket, outletId, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          backgroundColor: '#0C0F14',
          overflowY: 'hidden',
          height: '100%',
        }}
      >
        <CssBaseline />

        <AppBar position="fixed">
          <NavBar />
        </AppBar>

        {/* DESKTOP + TABLET DRAWER */}
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            display: {
              xs: 'none',
              sm: 'block',
            },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              bgcolor: '#0C0F14',
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar
            sx={{
              flexDirection: 'column',
              marginTop: '15px',
            }}
          >
            <Typography sx={{ color: '#fff' }} variant="h5">
              Xpanse
            </Typography>

            <Typography sx={{ color: '#fff' }} paragraph>
              POS
            </Typography>
          </Toolbar>

          <List className="sideBar">
            {menuItems.map((item) => (
              <ListItem
                key={item.text}
                className="sideBar-btnContainer"
                disablePadding
              >
                <ListItemButton
                  className="sideBar-button"
                  onClick={() => handleItemClick(item.route)}
                  sx={{
                    backgroundColor:
                      selectedRoute === item.route
                        ? '#003049'
                        : '#fff',
                    '&:hover': {
                      backgroundColor:
                        selectedRoute === item.route
                          ? '#003049'
                          : '#fff',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color:
                        selectedRoute === item.route
                          ? '#fff'
                          : '#000',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {item.icon}
{/* 
                    {item.text === 'Orders' && hasNewOrder && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 20,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'red',
                        }}
                      />
                    )} */}
                  </ListItemIcon>

                  <ListItemText
                    className="sideBar-btnText"
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: {
                        [theme.breakpoints.between('sm', 'md')]: {
                          fontSize: '8px',
                        },
                        [theme.breakpoints.up('md')]: {
                          fontSize: '11px',
                        },
                      },
                    }}
                    sx={{
                      margin: 0,
                      color:
                        selectedRoute === item.route
                          ? '#fff'
                          : '#000',
                      justifyContent: 'center',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

            <ListItem>
              <ListItemButton
                onClick={() => {
                  navigate('/profile');
                  setSelectedRoute('');
                }}
                sx={{
                  backgroundColor: '#0C0F14',
                  borderRadius: '4px',
                  margin: '5px 2px 0px 2px',
                  alignSelf: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: '#fff',
                    justifyContent: 'center',
                  }}
                >
                  <AccountCircleOutlinedIcon
                    sx={{ fontSize: '35px' }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary={userName}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: '16px',
                    },
                  }}
                  sx={{
                    color: '#fff',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem
              className="sideBar-btnContainer"
              disablePadding
              sx={{
                fontSize: '12px',
                margin: 0,
              }}
            >
              <ListItemButton
                onClick={handleButtonClick}
                className="sideBar-button"
                sx={{
                  backgroundColor: '#fff',
                  '&:hover': {
                    backgroundColor: '#003049',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: '#000',
                    justifyContent: 'center',
                    '&:hover': {
                      color: '#fff',
                    },
                  }}
                >
                  <LoginOutlinedIcon />
                </ListItemIcon>

                <ListItemText
                  primary="Logout"
                  className="sideBar-btnText"
                  primaryTypographyProps={{
                    sx: {
                      '&:hover': {
                        color: '#fff',
                      },
                      [theme.breakpoints.between('sm', 'md')]: {
                        fontSize: '8px',
                      },
                      [theme.breakpoints.up('md')]: {
                        fontSize: '11px',
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider />
        </Drawer>
      </Box>

      {/* MOBILE BOTTOM NAVIGATION */}
      <Box
        sx={{
          display: {
            xs: 'flex',
            sm: 'none',
          },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '64px',
          backgroundColor: '#0C0F14',
          zIndex: 1300,
          borderTop: '1px solid rgba(255,255,255,0.12)',
          px: 0.5,
        }}
      >
        {menuItems.map((item) => {
          const isSelected = selectedRoute === item.route;

          return (
            <Box
              key={item.text}
              onClick={() => handleItemClick(item.route)}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: isSelected ? '#fff' : '#b6b6b6',
                backgroundColor: isSelected
                  ? '#003049'
                  : 'transparent',
                borderRadius: '10px',
                my: 0.7,
                mx: 0.3,
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  '& svg': {
                    fontSize: '20px',
                  },
                }}
              >
                {item.icon}

                {/* FIXED MOBILE NOTIFICATION DOT */}
                {item.text === 'Orders' && hasNewOrder && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      backgroundColor: 'red',
                    }}
                  />
                )}
              </Box>

              <Typography
                sx={{
                  fontSize: '10px',
                  lineHeight: 1,
                  mt: 0.4,
                  maxWidth: '58px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.text}
              </Typography>
            </Box>
          );
        })}

        <Box
          onClick={() => {
            navigate('/profile');
            setSelectedRoute('');
          }}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#b6b6b6',
            borderRadius: '10px',
            my: 0.7,
            mx: 0.3,
            cursor: 'pointer',
          }}
        >
          <AccountCircleOutlinedIcon sx={{ fontSize: '20px' }} />

          <Typography
            sx={{
              fontSize: '10px',
              lineHeight: 1,
              mt: 0.4,
              maxWidth: '58px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Profile
          </Typography>
        </Box>
      </Box>
    </>
  );
}